import { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { createClient } from "@supabase/supabase-js";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, XCircle, RotateCcw, AlertTriangle, Loader2 } from "lucide-react";
import commandxLogo from "@/assets/commandx-logo.png";

// Use anon client — no auth required for public estimate viewing
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
const anonClient = createClient(supabaseUrl, supabaseAnonKey);

const fmt = (n: number | null | undefined) =>
  n != null ? `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2 })}` : "—";

const safeDate = (d: string | null | undefined) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }); }
  catch { return "—"; }
};

type PageState = "loading" | "view" | "approved" | "rejected" | "changes_requested" | "already_approved" | "already_rejected" | "expired" | "invalid";

interface EstimateData {
  id: string;
  estimate_number: string;
  customer_name: string | null;
  txn_date: string;
  expiry_date: string | null;
  status: string;
  line_items: any[];
  subtotal: number;
  total_amount: number;
  notes: string | null;
  share_token_expires_at: string;
  approved_at: string | null;
  approved_by_name: string | null;
  rejected_at: string | null;
  rejection_reason: string | null;
}

// Simple signature canvas
const SignaturePad = ({ onSignature }: { onSignature: (data: string) => void }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [drawing, setDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  const getPos = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    if ("touches" in e) {
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const startDraw = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    setDrawing(true);
    setHasDrawn(true);
    const ctx = canvasRef.current!.getContext("2d")!;
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!drawing) return;
    e.preventDefault();
    const ctx = canvasRef.current!.getContext("2d")!;
    const pos = getPos(e);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#1a1a2e";
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const endDraw = () => {
    setDrawing(false);
    if (hasDrawn && canvasRef.current) {
      onSignature(canvasRef.current.toDataURL());
    }
  };

  const clear = () => {
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext("2d")!;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
    onSignature("");
  };

  return (
    <div className="space-y-2">
      <div className="border-2 border-dashed rounded-lg overflow-hidden bg-white">
        <canvas
          ref={canvasRef}
          width={500}
          height={150}
          className="w-full cursor-crosshair touch-none"
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={endDraw}
          onMouseLeave={endDraw}
          onTouchStart={startDraw}
          onTouchMove={draw}
          onTouchEnd={endDraw}
        />
      </div>
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">Draw your signature above</p>
        <Button variant="ghost" size="sm" onClick={clear} type="button">Clear</Button>
      </div>
    </div>
  );
};

const EstimateApproval = () => {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<PageState>("loading");
  const [estimate, setEstimate] = useState<EstimateData | null>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [signature, setSignature] = useState("");
  const [action, setAction] = useState<"approve" | "reject" | "changes" | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) { setState("invalid"); return; }
    loadEstimate();
  }, [token]);

  const loadEstimate = async () => {
    const { data, error } = await anonClient.rpc("get_estimate_by_token", { p_token: token! });
    if (error || !data) { setState("invalid"); return; }
    const res = data as any;
    // Backend returns { expired: true, reason } for used/expired tokens
    if (res.expired) {
      setState("expired");
      return;
    }
    const est = res as unknown as EstimateData;
    setEstimate(est);
    if (est.status === "Accepted") setState("already_approved");
    else if (est.status === "Rejected") setState("already_rejected");
    else if (est.status === "Sent") setState("view");
    else setState("invalid");
  };

  const handleSubmit = async () => {
    if (!name.trim() || !email.trim()) return;
    setSubmitting(true);
    try {
      if (action === "approve") {
        const { data } = await anonClient.rpc("approve_estimate_by_token", {
          p_token: token!, p_name: name, p_email: email, p_signature: signature || null, p_notes: notes || null,
        });
        const res = data as any;
        if (res?.success) setState("approved");
        else throw new Error(res?.error || "Failed");
      } else if (action === "reject") {
        if (!notes.trim()) return;
        const { data } = await anonClient.rpc("reject_estimate_by_token", {
          p_token: token!, p_name: name, p_email: email, p_reason: notes,
        });
        const res = data as any;
        if (res?.success) setState("rejected");
        else throw new Error(res?.error || "Failed");
      } else if (action === "changes") {
        if (!notes.trim()) return;
        const { data } = await anonClient.rpc("request_changes_by_token", {
          p_token: token!, p_name: name, p_email: email, p_notes: notes,
        });
        const res = data as any;
        if (res?.success) setState("changes_requested");
        else throw new Error(res?.error || "Failed");
      }
    } catch (err: any) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  // Result pages
  if (state === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (state === "invalid") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center space-y-3">
            <AlertTriangle className="h-12 w-12 text-destructive mx-auto" />
            <h2 className="text-xl font-semibold">Invalid Link</h2>
            <p className="text-muted-foreground">This link is invalid or has been revoked.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (state === "expired") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center space-y-3">
            <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto" />
            <h2 className="text-xl font-semibold">Estimate Expired</h2>
            <p className="text-muted-foreground">This estimate has expired. Please contact us for an updated estimate.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (state === "approved") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center space-y-3">
            <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" />
            <h2 className="text-xl font-semibold">Estimate Approved!</h2>
            <p className="text-muted-foreground">We've been notified and will begin project setup.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (state === "rejected") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center space-y-3">
            <XCircle className="h-12 w-12 text-destructive mx-auto" />
            <h2 className="text-xl font-semibold">Estimate Declined</h2>
            <p className="text-muted-foreground">We've received your response. Thank you for your consideration.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (state === "changes_requested") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center space-y-3">
            <RotateCcw className="h-12 w-12 text-blue-500 mx-auto" />
            <h2 className="text-xl font-semibold">Changes Requested</h2>
            <p className="text-muted-foreground">Your feedback has been sent. We'll follow up shortly.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (state === "already_approved") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center space-y-3">
            <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" />
            <h2 className="text-xl font-semibold">Already Approved</h2>
            <p className="text-muted-foreground">
              This estimate was approved on {safeDate(estimate?.approved_at)} by {estimate?.approved_by_name}.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (state === "already_rejected") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center space-y-3">
            <XCircle className="h-12 w-12 text-destructive mx-auto" />
            <h2 className="text-xl font-semibold">Already Declined</h2>
            <p className="text-muted-foreground">This estimate was declined on {safeDate(estimate?.rejected_at)}.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main estimate view
  if (!estimate) return null;
  const lineItems = Array.isArray(estimate.line_items) ? estimate.line_items : [];

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-1">
          <h1 className="text-2xl font-bold tracking-tight" style={{ color: "hsl(222.2, 47.4%, 11.2%)" }}>
            Estimate
          </h1>
          <p className="text-lg text-muted-foreground font-medium">ESTIMATE</p>
        </div>

        <Card>
          <CardContent className="pt-6 space-y-6">
            {/* Meta */}
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-muted-foreground">Estimate Number</p>
                <p className="font-semibold font-mono">EST-{estimate.estimate_number}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Date</p>
                <p className="font-medium">{safeDate(estimate.txn_date)}</p>
              </div>
            </div>

            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-muted-foreground">Prepared For</p>
                <p className="font-semibold">{estimate.customer_name ?? "—"}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Valid Until</p>
                <p className="font-medium">{safeDate(estimate.expiry_date || estimate.share_token_expires_at)}</p>
              </div>
            </div>

            <Separator />

            {/* Line Items (Customer View) */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Line Items</h3>
              {lineItems.map((item: any, idx: number) => {
                const desc = item.customer_description || item.description || `Line ${idx + 1}`;
                const qty = item.qty ?? 0;
                const unit = item.unit || "";
                const totalBilled = item.total_billed ?? item.total ?? 0;
                const subLines = Array.isArray(item.sub_lines) ? item.sub_lines : [];
                const combinedUp = subLines.length > 0
                  ? subLines.reduce((s: number, sl: any) => s + (sl.unit_price ?? 0), 0)
                  : (totalBilled / (qty || 1));

                return (
                  <div key={idx} className="flex items-center justify-between py-2 border-b last:border-0">
                    <div>
                      <p className="font-medium text-sm">{idx + 1}. {desc}</p>
                      <p className="text-xs text-muted-foreground">{qty.toLocaleString()} {unit} @ {fmt(combinedUp)}</p>
                    </div>
                    <p className="font-semibold text-sm">{fmt(totalBilled)}</p>
                  </div>
                );
              })}
            </div>

            <Separator />

            {/* Totals */}
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span>{fmt(estimate.subtotal)}</span>
              </div>
              <div className="flex justify-between text-lg font-bold border-t pt-2 mt-2">
                <span>Total</span>
                <span>{fmt(estimate.total_amount)}</span>
              </div>
            </div>

            {estimate.notes && (
              <>
                <Separator />
                <div>
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-1">Notes</h3>
                  <p className="text-sm whitespace-pre-wrap">{estimate.notes}</p>
                </div>
              </>
            )}

            <Separator />

            {/* Response Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Your Response</h3>

              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="text-sm font-medium">Your Name *</label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" />
                </div>
                <div>
                  <label className="text-sm font-medium">Your Email *</label>
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@company.com" />
                </div>
              </div>

              {action === "approve" && (
                <div className="space-y-3 border rounded-lg p-4 bg-emerald-50/50">
                  <p className="text-sm font-medium">By signing, I approve this estimate and authorize the contractor to proceed.</p>
                  <SignaturePad onSignature={setSignature} />
                  <div>
                    <label className="text-sm font-medium">Notes (optional)</label>
                    <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any additional notes..." rows={2} />
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleSubmit} disabled={submitting || !name.trim() || !email.trim()} className="flex-1">
                      {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
                      Confirm Approval
                    </Button>
                    <Button variant="outline" onClick={() => setAction(null)}>Back</Button>
                  </div>
                </div>
              )}

              {action === "reject" && (
                <div className="space-y-3 border rounded-lg p-4 bg-red-50/50">
                  <div>
                    <label className="text-sm font-medium">Please let us know why you're declining *</label>
                    <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Reason for declining..." rows={3} />
                  </div>
                  <div className="flex gap-2">
                    <Button variant="destructive" onClick={handleSubmit} disabled={submitting || !name.trim() || !email.trim() || !notes.trim()} className="flex-1">
                      {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <XCircle className="h-4 w-4 mr-1" />}
                      Confirm Decline
                    </Button>
                    <Button variant="outline" onClick={() => setAction(null)}>Back</Button>
                  </div>
                </div>
              )}

              {action === "changes" && (
                <div className="space-y-3 border rounded-lg p-4 bg-blue-50/50">
                  <div>
                    <label className="text-sm font-medium">Please describe the changes needed *</label>
                    <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="What changes would you like?" rows={3} />
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleSubmit} disabled={submitting || !name.trim() || !email.trim() || !notes.trim()} className="flex-1" variant="secondary">
                      {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RotateCcw className="h-4 w-4 mr-1" />}
                      Submit Feedback
                    </Button>
                    <Button variant="outline" onClick={() => setAction(null)}>Back</Button>
                  </div>
                </div>
              )}

              {!action && (
                <div className="grid gap-2 sm:grid-cols-3">
                  <Button onClick={() => setAction("approve")} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                    <CheckCircle2 className="h-4 w-4 mr-1" /> Approve Estimate
                  </Button>
                  <Button variant="outline" onClick={() => setAction("changes")}>
                    <RotateCcw className="h-4 w-4 mr-1" /> Request Changes
                  </Button>
                  <Button variant="outline" className="text-destructive border-destructive/30 hover:bg-destructive/10" onClick={() => setAction("reject")}>
                    <XCircle className="h-4 w-4 mr-1" /> Decline
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="flex items-center justify-center gap-1.5">
          <img src={commandxLogo} alt="CommandX" className="h-4 object-contain" />
          <span className="text-xs text-muted-foreground">Powered by CommandX Ops Center</span>
        </div>
      </div>
    </div>
  );
};

export default EstimateApproval;
